import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Package, Search, ShieldCheck, Users, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, valuePoints, jcbCategories, forkliftCategories, trustIndicators } from '../mock.js';

const Home = () => {
  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi, I need information about spare parts.");
    window.open(`https://wa.me/${businessInfo.whatsapp}?text=${message}`, '_blank');
  };

  const iconMap = {
    package: Package,
    search: Search,
    'shield-check': ShieldCheck,
    users: Users
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-gray-100 py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMEM5RkYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzAtOS45NC04LjA2LTE4LTE4LTE4IiBzdHJva2U9IiMwMEM5RkYiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              JCB & Forklift Spare Parts.<br />
              <span className="text-[#FF6B7A]">Available When Your Machine Can't Wait.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-4 max-w-2xl mx-auto">
              Retail and wholesale supply of JCB backhoe loader and forklift spare parts.
            </p>
            <p className="text-base md:text-lg text-gray-600 mb-8">
              Correct parts, ready stock, quick response.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleWhatsApp}
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5A] text-white text-lg px-8 py-6"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp for Availability
              </Button>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00C9FF] text-[#00C9FF] hover:bg-[#00C9FF] hover:text-white text-lg px-8 py-6 w-full sm:w-auto"
                >
                  Request Price
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Here's the thing. Machines don't stop on schedule.
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              When they're down, you need the right part, not guesswork.
            </p>
            <p className="text-base text-gray-600">
              At <span className="font-semibold text-[#FF6B7A]">Sparecraft Solutions</span>, we deal only in JCB and forklift spare parts. Genuine, OEM-equivalent, and trusted aftermarket options — identified correctly and supplied fast.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valuePoints.map((point, index) => {
              const Icon = iconMap[point.icon];
              return (
                <Card key={index} className="border-2 hover:border-[#00C9FF] transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-br from-[#00C9FF]/10 to-[#FF6B7A]/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-[#00C9FF]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What We Deal In</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every part listed is enquiry-based, so you get availability and compatibility confirmed before you buy.
            </p>
          </div>

          {/* JCB Parts */}
          <div className="mb-16">
            <div className="mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">JCB Backhoe Loader Spare Parts</h3>
              <p className="text-gray-600">
                Engine parts, hydraulics, electricals, wear parts, filters, service kits, and more. Support for popular models like JCB 3DX and 3CX.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jcbCategories.map((category) => (
                <Card key={category.id} className="hover:shadow-lg transition-all border-2 hover:border-[#FF6B7A]">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="text-xl font-semibold text-gray-900">{category.name}</h4>
                      <div className="bg-[#FF6B7A]/10 p-2 rounded-lg">
                        <Package className="h-5 w-5 text-[#FF6B7A]" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                    <Link to="/products">
                      <Button variant="outline" size="sm" className="w-full border-[#FF6B7A] text-[#FF6B7A] hover:bg-[#FF6B7A] hover:text-white">
                        View Parts
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Forklift Parts */}
          <div>
            <div className="mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Forklift Spare Parts</h3>
              <p className="text-gray-600">
                Engine, hydraulic, brake, electrical, mast components, chains, rollers, tyres, and wear items. Parts for diesel, electric, and LPG forklifts.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forkliftCategories.map((category) => (
                <Card key={category.id} className="hover:shadow-lg transition-all border-2 hover:border-[#00C9FF]">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="text-xl font-semibold text-gray-900">{category.name}</h4>
                      <div className="bg-[#00C9FF]/10 p-2 rounded-lg">
                        <Package className="h-5 w-5 text-[#00C9FF]" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                    <Link to="/products">
                      <Button variant="outline" size="sm" className="w-full border-[#00C9FF] text-[#00C9FF] hover:bg-[#00C9FF] hover:text-white">
                        View Parts
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Bulk Buyers & Workshops</h2>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              If you're a workshop, dealer, or fleet operator, this is built for you.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
              {[
                'Consistent supply',
                'Competitive wholesale pricing',
                'Support in identifying correct parts',
                'Long-term supply relationships'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-[#00C9FF] flex-shrink-0 mt-1" />
                  <span className="text-base md:text-lg">{benefit}</span>
                </div>
              ))}
            </div>
            <Link to="/wholesale">
              <Button size="lg" className="bg-[#FF6B7A] hover:bg-[#FF5A6A] text-white text-lg px-8 py-6">
                Wholesale Enquiry
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why customers stick with Sparecraft Solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg border-2 border-gray-200">
                <div className="font-semibold text-[#FF6B7A] mb-2">{indicator.label}</div>
                <div className="text-sm text-gray-700">{indicator.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#FF6B7A] to-[#00C9FF] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need a part?</h2>
          <p className="text-lg md:text-xl mb-2">Don't waste time scrolling catalogs.</p>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Send us your machine model and requirement. We'll confirm the right part and availability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              className="bg-white text-[#FF6B7A] hover:bg-gray-100 text-lg px-8 py-6"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Chat on WhatsApp
            </Button>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#00C9FF] text-lg px-8 py-6"
              >
                Send Enquiry
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
